import React, { useState } from 'react';

import { useStripe, useElements } from '@stripe/react-stripe-js';
import { CardElement } from '@stripe/react-stripe-js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import StyledButton from '../styledComponents/StyledButton';
import {
  useAttachPaymentMethodMutation,
  useMakeDefaultPaymentMethodMutation,
} from '../../../features/billing/BillingApiSlice';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import StyledCheckbox from '../checkbox/StyledCheckbox';

const StyledCard = styled.div`
  width: 100%;
  margin: 0 auto;
  border: 1px solid #00000063;
  padding: 10px 10px;
  border-radius: 6px;
`;
const StyledCardError = styled.div`
  padding-top: 10px;
  color: red;
`;
const StyledCheckboxHolder = styled.div`
  padding-top: 10px;
  label > span:first-child {
    border-color: #00000063 !important;
  }
`;

/**
 * AddPaymentMethodForm component.
 *
 * @param {Object} props - The component props.
 * @param {Function} [props.afterSubmit] - The function to call after form submission.
 * @param {React.ReactElement} [props.customElement] - A custom element to render.
 * @returns {React.ReactElement} The rendered component.
 */
export default function AddPaymentMethodForm({
  afterSubmit = () => {},
  customElement = null,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);

  const [attachPaymentMethod, { isLoading }] = useAttachPaymentMethodMutation();
  const [makeDefaultPaymentMethod, { isLoading: defaultMethodUpdateLoading }] =
    useMakeDefaultPaymentMethodMutation();

  const handleSubmit = async (event) => {
    setLoading(true);
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      toast.error('Something went wrong');
      setError(error.message);
      setLoading(false);
    } else {
      const response = await attachPaymentMethod({
        id: paymentMethod?.id,
      });
      if (response?.data?.id === paymentMethod?.id && defaultPaymentMethod) {
        const res = await makeDefaultPaymentMethod({
          id: paymentMethod?.id,
          is_default: true,
        });
        if (res?.data?.id === paymentMethod?.id) {
          toast.success(
            'Payment Method Added Successfully and Set as Default.',
          );
          handleCloseModal(true);
        } else {
          toast.error('Something went wrong');
          handleCloseModal(true);
        }
      } else {
        if (response?.data?.id === paymentMethod?.id) {
          toast.success('Payment Method Added Successfully');
          handleCloseModal(true);
        } else {
          toast.error('Something went wrong');
          handleCloseModal(true);
        }
      }
    }
  };
  const handleCloseModal = (callAfterSubmit = false) => {
    setShowAddPaymentModal(false);
    setLoading(false);
    setError(null);
    setDefaultPaymentMethod(false);

    if (callAfterSubmit) afterSubmit();
  };

  return (
    <>
      {customElement ? (
        <div onClick={() => setShowAddPaymentModal(true)}> {customElement}</div>
      ) : (
        <StyledButton onClick={() => setShowAddPaymentModal(true)}>
          Add Payment Method
        </StyledButton>
      )}
      <Modal
        isOpen={showAddPaymentModal}
        toggle={() => handleCloseModal(false)}
      >
        <form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => handleCloseModal(false)}>
            Add Payment Method
          </ModalHeader>
          <ModalBody>
            <StyledCard>
              <CardElement
                options={{ hidePostalCode: true, disableLink: true }}
                disableLink={true}
              />
            </StyledCard>
            <StyledCheckboxHolder>
              <StyledCheckbox
                label="Use as default payment method"
                value={defaultPaymentMethod}
                onChange={() => setDefaultPaymentMethod(!defaultPaymentMethod)}
                disabled={isLoading || loading || defaultMethodUpdateLoading}
              />
            </StyledCheckboxHolder>
            {error && <StyledCardError>{error}</StyledCardError>}
          </ModalBody>
          <ModalFooter>
            <StyledButton
              disabled={!stripe}
              loading={isLoading || loading || defaultMethodUpdateLoading}
              type={'Submit'}
              color="primary"
            >
              Submit
            </StyledButton>{' '}
            <StyledButton
              type={'button'}
              color={'gray40'}
              onClick={() => handleCloseModal(false)}
              disabled={isLoading || loading || defaultMethodUpdateLoading}
            >
              Cancel
            </StyledButton>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
}
